import type { ClientProfileEntity, ClientProfileUpdateEntity } from '../entities/client-profile.entity';

export const CLIENT_EMAIL_MAX_LENGTH = 254;
export const CLIENT_COMMENTS_MAX_LENGTH = 2_000;

const CLIENT_EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const isClientEmailLike = (value: string): boolean => CLIENT_EMAIL_PATTERN.test(value);

const toRecordOrNull = (value: unknown): Record<string, unknown> | null => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return null;
    }

    return value as Record<string, unknown>;
};

export const createDefaultClientProfile = (): ClientProfileEntity => ({
    email: '',
    comments: '',
});

export const normalizeClientProfileEntity = (value: unknown): ClientProfileEntity => {
    const defaults = createDefaultClientProfile();
    const source = toRecordOrNull(value);
    if (!source) {
        return defaults;
    }

    return {
        email: typeof source.email === 'string' ? source.email : defaults.email,
        comments: typeof source.comments === 'string' ? source.comments : defaults.comments,
    };
};

export const normalizeClientEmailInput = (value: unknown, options: { allowUndefined: boolean }): string | undefined => {
    if (value === undefined) {
        if (options.allowUndefined) {
            return undefined;
        }

        throw new Error('Client e-mail is required');
    }

    if (value === null) {
        return '';
    }

    if (typeof value !== 'string') {
        throw new Error('Client e-mail must be a string');
    }

    const email = value.trim();
    if (!email) {
        return '';
    }

    if (email.length > CLIENT_EMAIL_MAX_LENGTH) {
        throw new Error(`Client e-mail must be at most ${CLIENT_EMAIL_MAX_LENGTH} characters`);
    }

    if (!isClientEmailLike(email)) {
        throw new Error('Client e-mail must be a valid e-mail address');
    }

    return email;
};

export const normalizeClientCommentsInput = (
    value: unknown,
    options: { allowUndefined: boolean },
): string | undefined => {
    if (value === undefined) {
        if (options.allowUndefined) {
            return undefined;
        }

        return '';
    }

    if (value === null) {
        return '';
    }

    if (typeof value !== 'string') {
        throw new Error('Client comments must be a string');
    }

    const comments = value.trim();
    if (comments.length > CLIENT_COMMENTS_MAX_LENGTH) {
        throw new Error(`Client comments must be at most ${CLIENT_COMMENTS_MAX_LENGTH} characters`);
    }

    return comments;
};

export const normalizeClientProfileUpdateEntity = (value: unknown): ClientProfileUpdateEntity => {
    const source = toRecordOrNull(value);
    if (!source) {
        throw new Error('Client profile must be an object');
    }

    const email = normalizeClientEmailInput(source.email, { allowUndefined: true });
    const comments = normalizeClientCommentsInput(source.comments, { allowUndefined: true });

    return {
        ...(email !== undefined ? { email } : {}),
        ...(comments !== undefined ? { comments } : {}),
    };
};
